/**
 * ProceduresGate: capability gate for Procedures pages.
 *
 * Route-level ProtectedRoute only knows roles, but Procedures capabilities are "role OR right"
 * (an editor holds ProceduresEditor; a staff member may hold Procedures.Runs.Read without a role).
 * This gate asks useProceduresPermissions instead.
 */

import type { ReactNode } from 'react';
import { IconLock } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { EmptyState } from '@/components/ui/data-display';
import { useProceduresPermissions, type ProceduresPermissions } from '@/hooks/procedures/useProceduresPermissions';

interface ProceduresGateProps {
  allow: (p: ProceduresPermissions) => boolean;
  children: ReactNode;
}

export function ProceduresGate({ allow, children }: ProceduresGateProps) {
  const perms = useProceduresPermissions();
  if (!allow(perms)) {
    return (
      <LayoutContainer>
        <EmptyState
          icon={<IconLock size={40} />}
          title="Access denied"
          description="You don't have permission to open this page. Ask a Procedures administrator for access."
        />
      </LayoutContainer>
    );
  }
  return <>{children}</>;
}
